import { useEffect, useState } from 'react'
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet'
import { Icon } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Shield, MapPin, Radio } from 'lucide-react'
import { FadeIn } from '../ui/FadeIn'
import { GlassCard } from '../ui/GlassCard'

// Tipos de guardias
interface Guard {
  id: string
  name: string
  position: [number, number]
  status: 'active' | 'patrolling' | 'checkpoint' | 'sos'
  lastUpdate: string
  battery: number
}

// Datos de ejemplo de guardias
const exampleGuards: Guard[] = [
  {
    id: '1',
    name: 'Guardia Alpha',
    position: [19.4326, -99.1332], // Ciudad de México
    status: 'patrolling',
    lastUpdate: 'Hace 2 min',
    battery: 85,
  },
  {
    id: '2',
    name: 'Guardia Beta',
    position: [19.4350, -99.1300],
    status: 'checkpoint',
    lastUpdate: 'Hace 1 min',
    battery: 92,
  },
  {
    id: '3',
    name: 'Guardia Gamma',
    position: [19.4300, -99.1350],
    status: 'active',
    lastUpdate: 'Hace 30 seg',
    battery: 78,
  },
  {
    id: '4',
    name: 'Guardia Delta',
    position: [19.4280, -99.1320],
    status: 'patrolling',
    lastUpdate: 'Hace 3 min',
    battery: 88,
  },
]

// Crear iconos personalizados para cada estado
const createGuardIcon = (status: Guard['status']) => {
  const colors: Record<Guard['status'], string> = {
    active: '#00FF88',
    patrolling: '#0066FF',
    checkpoint: '#FFD700',
    sos: '#FF0066',
  }

  return new Icon({
    iconUrl: `data:image/svg+xml;base64,${btoa(`
      <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
        <circle cx="16" cy="16" r="14" fill="${colors[status]}" opacity="0.9"/>
        <circle cx="16" cy="16" r="10" fill="white"/>
        <path d="M16 8 L18 14 L24 14 L19 18 L21 24 L16 20 L11 24 L13 18 L8 14 L14 14 Z" fill="${colors[status]}"/>
        <circle cx="16" cy="28" r="3" fill="${colors[status]}"/>
      </svg>
    `)}`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  })
}

export function SecurityMap() {
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  if (!isMounted) {
    return (
      <FadeIn>
        <GlassCard className="relative h-[600px] md:h-[700px] overflow-hidden rounded-2xl flex items-center justify-center">
          <div className="text-center text-gray-400">
            <Shield className="w-12 h-12 mx-auto mb-4 text-nexus-blue" />
            <p>Cargando mapa...</p>
          </div>
        </GlassCard>
      </FadeIn>
    )
  }

  return (
    <FadeIn>
      <GlassCard className="relative h-[600px] md:h-[700px] rounded-2xl p-0 overflow-hidden">
        <div className="w-full h-full relative">
          <MapContainer
            center={[19.4326, -99.1332]}
            zoom={14}
            style={{ height: '100%', width: '100%', minHeight: '600px' }}
            className="z-0"
            zoomControl={true}
            scrollWheelZoom={true}
            doubleClickZoom={true}
            dragging={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors. Data available under <a href="https://www.openstreetmap.org/copyright">ODbL</a>.'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
              minZoom={3}
            />

            {exampleGuards.map((guard) => (
              <div key={guard.id}>
                <Marker
                  position={guard.position}
                  icon={createGuardIcon(guard.status)}
                >
                  <Popup className="custom-popup">
                    <div className="text-white p-2 min-w-[200px]">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-5 h-5 text-nexus-blue" />
                        <h3 className="font-bold text-lg">{guard.name}</h3>
                      </div>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">Estado:</span>
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              guard.status === 'active'
                                ? 'bg-green-500/20 text-green-400'
                                : guard.status === 'patrolling'
                                  ? 'bg-blue-500/20 text-blue-400'
                                  : guard.status === 'checkpoint'
                                    ? 'bg-yellow-500/20 text-yellow-400'
                                    : 'bg-red-500/20 text-red-400'
                            }`}
                          >
                            {guard.status === 'active'
                              ? 'Activo'
                              : guard.status === 'patrolling'
                                ? 'Patrullando'
                                : guard.status === 'checkpoint'
                                  ? 'Checkpoint'
                                  : 'SOS'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-nexus-blue" />
                          <span className="text-xs">
                            {guard.position[0].toFixed(4)}, {guard.position[1].toFixed(4)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Radio className="w-4 h-4 text-nexus-blue" />
                          <span className="text-xs">Última actualización: {guard.lastUpdate}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full border-2 border-nexus-blue flex items-center justify-center">
                            <div
                              className={`w-2 h-2 rounded-full ${
                                guard.battery > 50 ? 'bg-green-500' : guard.battery > 20 ? 'bg-yellow-500' : 'bg-red-500'
                              }`}
                            />
                          </div>
                          <span className="text-xs">Batería: {guard.battery}%</span>
                        </div>
                      </div>
                    </div>
                  </Popup>
                </Marker>

                {/* Círculo de radio de acción */}
                <Circle
                  center={guard.position}
                  radius={200}
                  pathOptions={{
                    color: guard.status === 'sos' ? '#FF0066' : '#0066FF',
                    fillColor: guard.status === 'sos' ? '#FF0066' : '#0066FF',
                    fillOpacity: 0.1,
                    weight: 2,
                  }}
                />
              </div>
            ))}
          </MapContainer>
        </div>

        {/* Overlay con información */}
        <div className="absolute top-4 left-4 z-[1000] glass rounded-lg p-3 space-y-2 pointer-events-none">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-xs">Activo</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span className="text-xs">Patrullando</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span className="text-xs">Checkpoint</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="text-xs">SOS</span>
          </div>
        </div>
      </GlassCard>
    </FadeIn>
  )
}
